import { Trans, useLingui } from '@lingui/react/macro'
import { Box, Card, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { useDeveloperMode } from '@markku/components'
import { isTruthy } from '@markku/utils'
import {
  IconBook,
  IconChartLine,
  IconChevronRight,
  IconCoinEuro,
  IconCube,
  IconFileText,
  IconHome,
  IconShield,
  IconShoppingCart,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import React from 'react'
import { SUBSCRIPTION_SALES_ENABLED } from '../../../config/features'
import { propertyHooks } from '../../../lib/trpcHooks/propertyHooks'

interface ActionItemProps {
  icon: React.ReactNode
  label: string
  description: string
  href?: string
  onClick?: () => void
}

function ActionItem({
  icon,
  label,
  description,
  href,
  onClick,
}: ActionItemProps) {
  const content = (
    <Card
      radius="md"
      withBorder
      p="sm"
      style={{
        transition: 'all 0.2s',
        cursor: 'pointer',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateX(4px)',
            borderColor: 'var(--mantine-color-brandOrange-3)',
          },
        },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" wrap="nowrap">
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--mantine-color-gray-0)',
            }}
          >
            {icon}
          </Box>
          <Stack gap={0}>
            <Text fw={600}>{label}</Text>
            <Text c="dimmed">{description}</Text>
          </Stack>
        </Group>
        <IconChevronRight size={18} color="var(--mantine-color-gray-5)" />
      </Group>
    </Card>
  )

  if (href) {
    return (
      <UnstyledButton component={Link} to={href} style={{ width: '100%' }}>
        {content}
      </UnstyledButton>
    )
  }

  return (
    <UnstyledButton onClick={onClick} style={{ width: '100%' }}>
      {content}
    </UnstyledButton>
  )
}

export function QuickActionsList() {
  const { t } = useLingui()
  const { isDeveloperMode } = useDeveloperMode()
  const { data: propertiesData } = propertyHooks.useList()
  const firstProperty = propertiesData?.data?.[0]
  const hasSubscription = propertiesData?.data.some((property) =>
    property.subscriptions?.some((sub) => sub.status === 'active'),
  )
  const [,] = React.useState(false)

  const actions = [
    {
      icon: <IconBook size={20} color="var(--mantine-color-dark-7)" />,
      label: t`Digital maintenance log`,
      description: t`View maintenance history and tasks`,
      link: '/maintenance',
    },
    {
      icon: <IconHome size={20} color="var(--mantine-color-blue-7)" />,
      label: t`Property details`,
      description: t`View property details`,
      // If property doesn't exist, link to maintenance (which asks to enter property)
      link: firstProperty ? `/properties/${firstProperty.id}` : '/maintenance',
    },
    {
      icon: <IconCoinEuro size={20} color="var(--mantine-color-yellow-7)" />,
      label: t`Cost tracking`,
      description: t`Budgeting and household deduction`,
      link: `/maintenance-costs${firstProperty ? `?propertyId=${firstProperty.id}` : ''}`,
    },
    {
      icon: <IconChartLine size={20} color="var(--mantine-color-teal-7)" />,
      label: t`Consumption tracking`,
      description: t`Electricity, water and heating`,
      link: `/consumption${firstProperty ? `?propertyId=${firstProperty.id}` : ''}`,
    },
    {
      icon: <IconFileText size={20} color="var(--mantine-color-orange-7)" />,
      label: t`Documents`,
      description: t`Certificates and user manuals`,
      link: firstProperty
        ? `/properties/${firstProperty.id}#documents`
        : '/maintenance',
    },
    {
      icon: <IconShield size={20} color="var(--mantine-color-green-7)" />,
      label: t`Warranties`,
      description: t`Warranty certificates and reminders`,
      link: firstProperty
        ? `/properties/${firstProperty.id}#warranties`
        : '/maintenance',
    },
    isDeveloperMode && {
      icon: <IconShoppingCart size={20} color="var(--mantine-color-green-7)" />,
      label: t`Order additional services`,
      description: t`Get additional help at home`,
      link: '/maintenance-services',
    },
    (hasSubscription || SUBSCRIPTION_SALES_ENABLED) && {
      icon: <IconCube size={20} color="var(--mantine-color-violet-7)" />,
      label: t`Your Markku package`,
      description: t`Manage your subscription`,
      link: '/maintenance-services',
    },
  ].filter(isTruthy)

  return (
    <Stack gap="md">
      <Text fw={600}>
        <Trans>Quick actions</Trans>
      </Text>
      <Stack gap="xs">
        {actions.map((action, index) => (
          <ActionItem
            key={index}
            icon={action.icon}
            label={action.label}
            description={action.description}
            href={action.link}
          />
        ))}
      </Stack>
    </Stack>
  )
}
